import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { XeroReauthorizeModalComponent } from './xero-reauthorize-modal/xero-reauthorize-modal.component';

@Component({
  selector: 'app-admin-utilities',
  templateUrl: './admin-utilities.component.html',
  styleUrls: ['./admin-utilities.component.css'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatSelectModule, MatFormFieldModule, MatExpansionModule, MatTableModule, MatDialogModule, FormsModule]
})
export class AdminUtilitiesComponent implements OnInit, OnDestroy {
  userId = sessionStorage.getItem('UserId');

  isProcessing = false;
  processingTask = '';

  // Email batch progress
  emailSessionId: string | null = null;
  emailTotal = 0;
  emailSent = 0;
  emailFailed = 0;
  emailRemaining = 0;
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  
  // Invoice batch form fields
  selectedServiceTypeId: number | null = null;
  selectedFrequency: string | null = null;
  
  // Preview data
  alertsPreviewData: any[] = [];
  invoicesPreviewData: any[] = [];
  showAlertsPreview = false;
  showInvoicesPreview = false;
  loadingAlertsPreview = false;
  loadingInvoicesPreview = false;
  
  // Table column definitions
  alertsColumns = ['clientName', 'subscriptionName', 'contactName', 'email', 'notificationCountInAlert'];
  invoicesColumns = ['clientName', 'subscriptionName', 'purchaseOrderReference', 'notificationCount'];
  
  // Dropdown options
  serviceTypes = [
    { label: 'Named', value: 1 },
    { label: 'Discretionary', value: 2 },
    { label: 'Trust', value: 3 }
  ];
  
  frequencies = [
    { label: 'Monthly', value: 'M' },
    { label: 'Quarterly', value: 'Q' },
    { label: 'Annual', value: 'A' }
  ];

  // Test mode settings
  alertsTestMode = false;
  invoicesTestMode = false;
  testEmailSendToAddress: string | null = null;
  testFinanceContactId: string | null = null;

  constructor(private http: HttpClient, private snackBar: MatSnackBar, private dialog: MatDialog) {
    this.loadTestModeSettings();
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private loadTestModeSettings(): void {
    this.http.get<any>('charitycentral/GetTestModeSettings').subscribe({
      next: (settings) => {
        this.alertsTestMode = settings.alertsTestMode;
        this.invoicesTestMode = settings.invoicesTestMode;
        this.testEmailSendToAddress = settings.testEmailSendToAddress ?? null;
        this.testFinanceContactId = settings.testFinanceContactId ?? null;
      },
      error: (error) => {
        console.error('Error loading test mode settings:', error);
      }
    });
  }

  runNotifications(): void {
    this.isProcessing = true;
    this.processingTask = 'Starting Alerts';
    this.emailSessionId = null;
    this.emailTotal = this.alertsPreviewData.length; // use preview count as initial estimate
    this.emailSent = 0;
    this.emailFailed = 0;
    this.emailRemaining = this.emailTotal;

    this.http.get<{ sessionId: string }>(`charitycentral/RunScheduledAlertBatch?UserId=${this.userId}`).subscribe({
      next: (response) => {
        this.emailSessionId = response.sessionId;
        this.processingTask = 'Sending Alerts';
        this.startPolling();
      },
      error: (error) => {
        this.isProcessing = false;
        this.snackBar.open(`Error starting alerts: ${error.message}`, 'Close', { duration: 5000 });
      }
    });
  }

  private startPolling(): void {
    this.pollInterval = setInterval(() => {
      if (!this.emailSessionId) return;
      this.http.get<any>(`charitycentral/GetAlertBatchProgress?sessionId=${this.emailSessionId}`).subscribe({
        next: (progress) => {
          if (progress.total > 0) this.emailTotal = progress.total;
          this.emailSent = progress.sent;
          this.emailFailed = progress.failed;
          this.emailRemaining = progress.remaining;

          if (progress.isComplete) {
            this.stopPolling();
            this.isProcessing = false;
            if (progress.errorMessage) {
              this.snackBar.open(`Alerts failed: ${progress.errorMessage}`, 'Close', { duration: 10000 });
            } else {
              const msg = progress.failed > 0
                ? `Alerts completed: ${progress.sent} sent, ${progress.failed} failed`
                : `Alerts completed: ${progress.sent} emails sent successfully`;
              this.snackBar.open(msg, 'Close', { duration: 8000 });
            }
          }
        },
        error: () => { /* ignore transient polling errors */ }
      });
    }, 3000);
  }

  private stopPolling(): void {
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  runInvoices(): void {
    // Validate that both dropdowns are selected
    if (this.selectedServiceTypeId === null || this.selectedFrequency === null) {
      this.snackBar.open('Please select both Service Type and Frequency', 'Close', { duration: 5000 });
      return;
    }

    this.isProcessing = true;
    this.processingTask = 'Running Invoices';

    const url = `charitycentral/RunScheduledInvoiceBatch?ServiceTypeId=${this.selectedServiceTypeId}&Frequency=${this.selectedFrequency}&UserId=${this.userId}`;

    this.http.get<any>(url).subscribe({
      next: () => {
        this.isProcessing = false;
        this.snackBar.open('Invoices batch completed successfully', 'Close', { duration: 5000 });
      },
      error: (error) => {
        this.isProcessing = false;
        
        // Check if this is a token expiration error
        const isTokenError = 
          (error.status === 401 && error.error?.requiresReauthorization) ||
          (error.status === 500 && error.error?.message && 
           (error.error.message.toLowerCase().includes('token') || 
            error.error.message.toLowerCase().includes('reauthorization') ||
            error.error.message.toLowerCase().includes('authorization')));

        if (isTokenError) {
          this.snackBar.open('Xero authorization expired. Please reauthorize to continue.', 'Close', { duration: 5000 });
          this.openXeroReauthorizeModal();
        } else {
          this.snackBar.open(`Error running invoices: ${error.error?.message || error.message}`, 'Close', { duration: 5000 });
          console.error('Error:', error);
        }
      }
    });
  }

  private openXeroReauthorizeModal(): void {
    this.dialog.open(XeroReauthorizeModalComponent, {
      width: '500px',
      disableClose: true,
      panelClass: 'xero-reauthorize-dialog'
    });
  }

  previewAlerts(): void {
    this.loadingAlertsPreview = true;
    this.http.get(`charitycentral/GetAlertsPreview?UserId=${this.userId}`).subscribe({
      next: (data: any) => {
        this.alertsPreviewData = data;
        this.showAlertsPreview = true;
        this.loadingAlertsPreview = false;
        if (data.length === 0) {
          this.snackBar.open('No alerts to preview', 'Close', { duration: 5000 });
        }
      },
      error: (error) => {
        this.loadingAlertsPreview = false;
        this.snackBar.open(`Error loading alerts preview: ${error.message}`, 'Close', { duration: 5000 });
        console.error('Error:', error);
      }
    });
  }

  previewInvoices(): void {
    if (this.selectedServiceTypeId === null || this.selectedFrequency === null) {
      this.snackBar.open('Please select both Service Type and Frequency', 'Close', { duration: 5000 });
      return;
    }

    this.loadingInvoicesPreview = true;
    const url = `charitycentral/GetInvoicesPreview?ServiceTypeId=${this.selectedServiceTypeId}&Frequency=${this.selectedFrequency}&UserId=${this.userId}`;

    this.http.get(url).subscribe({
      next: (data: any) => {
        this.invoicesPreviewData = data;
        this.showInvoicesPreview = true;
        this.loadingInvoicesPreview = false;
        if (data.length === 0) {
          this.snackBar.open('No invoices to preview', 'Close', { duration: 5000 });
        }
      },
      error: (error) => {
        this.loadingInvoicesPreview = false;
        this.snackBar.open(`Error loading invoices preview: ${error.message}`, 'Close', { duration: 5000 });
        console.error('Error:', error);
      }
    });
  }
}
